import { Button } from '@latitude/button';
import type { CustomClassNames } from 'components/ComponentsTypes';
import useDatepickerContext from 'hooks/useDatepickerContext';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDayPicker } from 'react-day-picker';
import { isValidDate } from 'utils/dateUtils';

import { PeriodToggle } from './PeriodToggle';
import { TimeSpinner } from './TimeSpinner';
import type { TimePeriod } from './types';
import { clampValue, to12Hour, to24Hour } from './utils';

const SYNC_DEBOUNCE_MS = 500;

export const TimePicker = (): JSX.Element => {
  const { onDayClick, onOpenChange, value } = useDatepickerContext();
  const { selected, ...daypicker } = useDayPicker();
  const classNames = daypicker.classNames as CustomClassNames;

  // Initialize state from selected date or defaults
  const initialTime =
    selected instanceof Date
      ? to12Hour(selected.getHours())
      : { hour: 12, period: 'AM' as TimePeriod };

  const [hour, setHour] = useState(initialTime.hour);
  const [minute, setMinute] = useState(
    selected instanceof Date ? selected.getMinutes() : 0,
  );
  const [period, setPeriod] = useState<TimePeriod>(initialTime.period);

  // Track if we're the source of the change to avoid sync loops
  const isInternalUpdate = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync internal state when external selected value changes (debounced)
  useEffect(() => {
    // Skip if we triggered the change ourselves
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the sync to handle manual input typing
    debounceTimerRef.current = setTimeout(() => {
      if (selected instanceof Date) {
        const newTime = to12Hour(selected.getHours());
        setHour(newTime.hour);
        setMinute(selected.getMinutes());
        setPeriod(newTime.period);
      }
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [selected]);

  const updateTime = (
    newHour: number,
    newMinute: number,
    newPeriod: TimePeriod,
  ) => {
    const baseDate = selected instanceof Date ? selected : new Date();
    const newDate = new Date(baseDate);
    newDate.setHours(to24Hour(newHour, newPeriod));
    newDate.setMinutes(newMinute);
    newDate.setSeconds(0);
    // Mark as internal update to prevent sync loop
    isInternalUpdate.current = true;
    onDayClick(newDate);
  };

  // Hour handlers
  const handleHourChange = (value: string) => {
    // Valid hours in 12-hour format: 1-12
    const newHour = clampValue(value, 1, 12);
    setHour(newHour);
    updateTime(newHour, minute, period);
  };

  const increaseHour = () => {
    const newHour = hour === 12 ? 1 : hour + 1;
    setHour(newHour);
    updateTime(newHour, minute, period);
  };

  const decreaseHour = () => {
    const newHour = hour === 1 ? 12 : hour - 1;
    setHour(newHour);
    updateTime(newHour, minute, period);
  };

  // Minute handlers
  const handleMinuteChange = (value: string) => {
    // Valid minutes: 0-59
    const newMinute = clampValue(value, 0, 59);
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  };

  const increaseMinute = () => {
    const newMinute = minute === 59 ? 0 : minute + 1;
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  };

  const decreaseMinute = () => {
    const newMinute = minute === 0 ? 59 : minute - 1;
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  };

  // Period handler
  const togglePeriod = () => {
    const newPeriod: TimePeriod = period === 'AM' ? 'PM' : 'AM';
    setPeriod(newPeriod);
    updateTime(hour, minute, newPeriod);
  };

  const rowHeight = classNames.time_picker_row_height;

  return (
    <div
      className={classNames.time_picker_container}
      role="group"
      aria-label="Time selection"
    >
      <div
        className={classNames.time_picker_row}
        role="group"
        aria-label="Time inputs"
      >
        <TimeSpinner
          value={hour}
          onChange={handleHourChange}
          onBlur={() => handleHourChange(String(hour))}
          onIncrement={increaseHour}
          onDecrement={decreaseHour}
          rowHeight={rowHeight}
          min={1}
          max={12}
          label="Hour"
        />
        <TimeSpinner
          value={minute}
          onChange={handleMinuteChange}
          onBlur={() => handleMinuteChange(String(minute))}
          onIncrement={increaseMinute}
          onDecrement={decreaseMinute}
          rowHeight={rowHeight}
          min={0}
          max={59}
          label="Minute"
        />
        <PeriodToggle
          period={period}
          onToggle={togglePeriod}
          rowHeight={rowHeight}
          daypickerClasses={classNames}
        />
      </div>
      <div
        className={`${classNames.time_picker_row} justify-end`}
        role="group"
        aria-label="Actions"
      >
        <Button
          className={rowHeight}
          variant="neutral"
          htmlType="button"
          aria-label="Cancel time selection"
          onClick={() => {
            onOpenChange(false);
            if (isValidDate(value)) {
              onDayClick(value);
            }
          }}
        >
          Cancel
        </Button>
        <Button
          className={rowHeight}
          variant="primary"
          htmlType="button"
          aria-label="Confirm time selection"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          Select
        </Button>
      </div>
    </div>
  );
};
